import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Gift } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import GiftModal from './GiftModal';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const menuRef = useRef(null);
  const buttonRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };
  
  // Function to handle clicks outside of menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isMenuOpen && 
        menuRef.current && 
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        closeMenu();
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  // Handle scroll and update active section
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['packages', 'testimonials', 'contact'];
      const headerHeight = document.querySelector('header')?.offsetHeight || 0;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= headerHeight + 100 && rect.bottom >= headerHeight + 100) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle navigation for both anchor links and routes
  const handleNavigation = (e, targetId, isRoute = false) => {
    e.preventDefault();
    closeMenu();

    if (isRoute) {
      navigate(targetId);
      return;
    }

    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete before scrolling
      setTimeout(() => {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          const headerHeight = document.querySelector('header').offsetHeight;
          const scrollPadding = 10;
          const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: targetPosition - headerHeight - scrollPadding,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      // If we're already on the home page, just scroll
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const headerHeight = document.querySelector('header').offsetHeight;
        const scrollPadding = 10;
        const targetPosition = targetElement.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: targetPosition - headerHeight - scrollPadding,
          behavior: 'smooth'
        });
      }
    }
  };

  const isActive = (section) => {
    if (section === 'about' && location.pathname === '/about') {
      return true;
    }
    if (section === 'blog' && location.pathname.startsWith('/blog')) {
      return true;
    }
    if (section === 'faq' && location.pathname === '/faq') {
      return true;
    }
    return activeSection === section;
  };

  return (
    <header className="bg-white sticky top-0 z-40 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex-shrink-0">
            <a 
              href="/" 
              className="flex items-center" 
              onClick={(e) => {
                e.preventDefault();
                closeMenu();
                navigate('/');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <img src="/logo.png" alt="PHYT GYM Logo" className="h-8 w-auto mr-2" />
              <div className="text-2xl font-bold text-primary-500 mr-2">THE PHYT GYM</div>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            <a 
              href="/about" 
              className={`text-gray-700 hover:text-primary-500 transition ${isActive('about') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, '/about', true)}
            >
              About
            </a>
            <a 
              href="#packages" 
              className={`text-gray-700 hover:text-primary-500 transition ${isActive('packages') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, 'packages')}
            >
              Programs & Services
            </a>
            <a 
              href="#testimonials" 
              className={`text-gray-700 hover:text-primary-500 transition ${isActive('testimonials') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, 'testimonials')}
            >
              Testimonials
            </a>
            <a 
              href="/blog" 
              className={`text-gray-700 hover:text-primary-500 transition ${isActive('blog') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, '/blog', true)}
            >
              Blog
            </a>
            <a 
              href="/faq" 
              className={`text-gray-700 hover:text-primary-500 transition ${isActive('faq') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, '/faq', true)}
            >
              FAQ
            </a>
            
            <button 
              onClick={() => setIsGiftModalOpen(true)}
              className="bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-2 rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg relative overflow-hidden group flex items-center space-x-2"
            >
              <Gift size={18} />
              <span className="relative z-10">Gift & Referral</span>
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </button>
          </nav>

          {/* Login Button */}
          <div className="hidden md:block">
            <a 
              href="#contact" 
              className="bg-primary-500 text-white px-5 py-2 rounded-lg hover:bg-primary-600 transition"
              onClick={(e) => handleNavigation(e, 'contact')}
            >
              Book Session
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button 
              type="button" 
              className="text-gray-700"
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              ref={buttonRef}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden bg-white border-t" ref={menuRef}>
          <div className="px-4 py-2 space-y-3">
            <a 
              href="/about" 
              className={`block py-2 text-gray-700 hover:text-primary-500 ${isActive('about') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, '/about', true)}
            >
              About
            </a>
            <a 
              href="#packages" 
              className={`block py-2 text-gray-700 hover:text-primary-500 ${isActive('packages') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, 'packages')}
            >
              Programs & Services
            </a>
            <a 
              href="#testimonials" 
              className={`block py-2 text-gray-700 hover:text-primary-500 ${isActive('testimonials') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, 'testimonials')}
            >
              Testimonials
            </a>
            <a 
              href="/blog" 
              className={`block py-2 text-gray-700 hover:text-primary-500 ${isActive('blog') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, '/blog', true)}
            >
              Blog
            </a>
            <a 
              href="/faq" 
              className={`block py-2 text-gray-700 hover:text-primary-500 ${isActive('faq') ? 'text-primary-500 font-medium' : ''}`}
              onClick={(e) => handleNavigation(e, '/faq', true)}
            >
              FAQ
            </a>
            <a 
              href="#contact" 
              className="block py-2 text-gray-700 hover:text-primary-500 transition"
              onClick={(e) => handleNavigation(e, 'contact')}
            >
              Contact
            </a>
            
            <button 
              onClick={() => {
                setIsGiftModalOpen(true);
                closeMenu();
              }}
              className="w-full mt-4 bg-gradient-to-r from-primary-500 to-primary-600 text-white px-6 py-3 rounded-xl font-medium hover:from-primary-600 hover:to-primary-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg relative overflow-hidden group flex items-center justify-center space-x-2"
            >
              <Gift size={18} />
              <span className="relative z-10">Gift & Referral</span>
              <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
            </button>
          </div>
        </div>
      )}

      <GiftModal isOpen={isGiftModalOpen} onClose={() => setIsGiftModalOpen(false)} />
    </header>
  );
};

export default Header;